import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'sonner';
import { FileText, MagnifyingGlass, PencilSimple, Plus, Question, Trash } from 'phosphor-react';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { Tour, TourPasso, useTour } from '../../../../components/Tour';
import { useAuth } from '../../../../providers/auth';
import { IEmpresaSn, IFatura, TELA_FATURAS_PATH, mensagemErro, snApi } from './api';
import { brl, cnpjFmt, dataBr } from './regras';

const TIPOS: Record<IFatura['tipo'], string> = {
  servico: 'Serviço',
  revenda: 'Revenda de mercadoria',
  industrializacao: 'Venda de industrializado',
  locacao: 'Locação sem ISS',
};

const COR_TIPO: Record<IFatura['tipo'], string> = {
  servico: 'blue',
  revenda: 'purple',
  industrializacao: 'purple',
  locacao: 'teal',
};

const PASSOS: TourPasso[] = [
  {
    alvo: 'mes',
    titulo: 'Mês da receita',
    texto: 'A fatura entra na apuração do mês escolhido aqui (regime de competência do Simples).',
  },
  {
    alvo: 'nova',
    titulo: 'Lançar fatura',
    texto:
      'Use para toda receita sem XML: fatura de locação, recibo, serviço sem NFS-e. Ela soma na receita do mês e o DAS é recalculado na hora.',
  },
  {
    alvo: 'tipo',
    titulo: 'Tipo e item da LC 116',
    texto:
      'O tipo decide o anexo: serviço vai pelo item da LC 116 (e Fator R quando couber); revenda Anexo I; industrializado Anexo II; locação de bens móveis Anexo III sem ISS. ISS retido e ST tiram o tributo do DAS.',
  },
  {
    alvo: 'lista',
    titulo: 'Conferência',
    texto:
      'Se a apuração já estava aprovada, a aprovação cai e precisa aprovar de novo. Mês transmitido não aceita fatura nova: só retificação.',
  },
];

const VAZIA = (empresaId: number, competencia: string): IFatura => ({
  empresa_id: empresaId,
  competencia,
  data: competencia,
  tipo: 'servico',
  descricao: '',
  numero_documento: null,
  tomador_doc: null,
  tomador_nome: null,
  valor: 0,
  item_lc116: null,
  iss_retido: false,
  municipio_ibge: null,
  icms_st: false,
  monofasico: false,
  exterior: false,
  observacao: null,
});

/** Busca de empresa do Simples (carteira do usuário). */
function EmpresaBusca({
  competencia,
  valor,
  onChange,
  isDisabled,
}: {
  competencia: string;
  valor: IEmpresaSn | null;
  onChange: (e: IEmpresaSn | null) => void;
  isDisabled?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [termo] = useDebounceValue(texto, 350);
  const { data, isFetching } = useQuery({
    queryKey: ['sn-empresas-busca', competencia, termo],
    queryFn: () => snApi.listar({ competencia, search: termo || undefined, size: 15 }),
    enabled: !valor && termo.length >= 2,
  });
  if (valor) {
    return (
      <Flex align="center" gap={2} p={2} border="1px solid #E2E8F0" borderRadius="8px">
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight={600} noOfLines={1}>
            {valor.nome}
          </Text>
          <Text fontSize="11px" color="#64748B">
            ID {valor.external_id} · {cnpjFmt(valor.cnpj)}
          </Text>
        </Box>
        {!isDisabled && (
          <Button size="xs" variant="ghost" onClick={() => onChange(null)}>
            Trocar
          </Button>
        )}
      </Flex>
    );
  }
  return (
    <Box position="relative">
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          {isFetching ? <Spinner size="xs" /> : <MagnifyingGlass size={14} color="#94A3B8" />}
        </InputLeftElement>
        <Input
          placeholder="Nome, CNPJ ou ID"
          value={texto}
          onChange={e => setTexto(e.target.value)}
          autoFocus
        />
      </InputGroup>
      {!!data?.items.length && (
        <List
          position="absolute"
          zIndex={10}
          bg="white"
          w="full"
          mt={1}
          border="1px solid #E2E8F0"
          borderRadius="8px"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
        >
          {data.items.map(i => (
            <ListItem
              key={i.empresa.id}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'blue.50' }}
              onClick={() => onChange(i.empresa)}
            >
              <Text fontSize="13px">{i.empresa.nome}</Text>
              <Text fontSize="11px" color="#64748B">
                ID {i.empresa.external_id} · {cnpjFmt(i.empresa.cnpj)}
              </Text>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

function FaturaModal({
  inicial,
  empresaInicial,
  competencia,
  onFechar,
}: {
  inicial: IFatura;
  empresaInicial: IEmpresaSn | null;
  competencia: string;
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState<IFatura>(inicial);
  const [empresa, setEmpresa] = useState<IEmpresaSn | null>(empresaInicial);
  const set = (p: Partial<IFatura>) => setF(v => ({ ...v, ...p }));
  const servico = f.tipo === 'servico';
  const locacao = f.tipo === 'locacao';
  const produto = !servico && !locacao;
  const salvar = useMutation({
    mutationFn: () => {
      const body: IFatura = {
        ...f,
        empresa_id: empresa!.id,
        competencia,
        item_lc116: servico ? f.item_lc116 : null,
        iss_retido: servico && f.iss_retido,
        municipio_ibge: servico ? f.municipio_ibge : null,
        icms_st: produto && f.icms_st,
        monofasico: produto && f.monofasico,
      };
      delete body.empresa;
      return f.id ? snApi.editarFatura(f.id, body) : snApi.criarFatura(body);
    },
    onSuccess: () => {
      toast.success('Fatura salva e apuração recalculada.');
      qc.invalidateQueries({ queryKey: ['sn-faturas'] });
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
      qc.invalidateQueries({ queryKey: ['sn-totais'] });
      onFechar();
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const valido = !!empresa && f.descricao.trim().length >= 2 && f.valor > 0 && !!f.data;
  return (
    <Modal isOpen onClose={onFechar} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">{f.id ? 'Editar fatura' : 'Nova fatura'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb={3}>
            <FormLabel fontSize="12px" mb={1}>
              Empresa
            </FormLabel>
            <EmpresaBusca
              competencia={competencia}
              valor={empresa}
              onChange={setEmpresa}
              isDisabled={!!f.id}
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Tipo
              </FormLabel>
              <Select
                size="sm"
                value={f.tipo}
                onChange={e => set({ tipo: e.target.value as IFatura['tipo'] })}
              >
                {Object.entries(TIPOS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Data
              </FormLabel>
              <Input
                size="sm"
                type="date"
                value={f.data}
                onChange={e => set({ data: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="12px" mb={1}>
                Valor (R$)
              </FormLabel>
              <Input
                size="sm"
                type="number"
                step="0.01"
                min={0}
                value={f.valor || ''}
                onChange={e => set({ valor: Number(e.target.value) })}
              />
            </FormControl>
          </SimpleGrid>
          <FormControl isRequired mt={3}>
            <FormLabel fontSize="12px" mb={1}>
              Descrição
            </FormLabel>
            <Input
              size="sm"
              value={f.descricao}
              onChange={e => set({ descricao: e.target.value })}
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                Nº do documento
              </FormLabel>
              <Input
                size="sm"
                value={f.numero_documento ?? ''}
                onChange={e => set({ numero_documento: e.target.value || null })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                CPF/CNPJ do tomador
              </FormLabel>
              <Input
                size="sm"
                value={f.tomador_doc ?? ''}
                onChange={e => set({ tomador_doc: e.target.value || null })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="12px" mb={1}>
                Nome do tomador
              </FormLabel>
              <Input
                size="sm"
                value={f.tomador_nome ?? ''}
                onChange={e => set({ tomador_nome: e.target.value || null })}
              />
            </FormControl>
          </SimpleGrid>
          {servico && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={3}>
              <FormControl>
                <FormLabel fontSize="12px" mb={1}>
                  Item da LC 116 (ex.: 7.02)
                </FormLabel>
                <Input
                  size="sm"
                  value={f.item_lc116 ?? ''}
                  onChange={e => set({ item_lc116: e.target.value || null })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" mb={1}>
                  Município do serviço (IBGE, se outro)
                </FormLabel>
                <Input
                  size="sm"
                  value={f.municipio_ibge ?? ''}
                  onChange={e => set({ municipio_ibge: e.target.value || null })}
                />
              </FormControl>
              <Checkbox
                isChecked={f.iss_retido}
                onChange={e => set({ iss_retido: e.target.checked })}
              >
                ISS retido pelo tomador
              </Checkbox>
            </SimpleGrid>
          )}
          {locacao && (
            <Text mt={3} fontSize="12px" color="#64748B">
              Locação de bens móveis: entra no Anexo III sem incidência do ISSQN (o ISS sai do DAS).
              Não usa item da LC 116 nem Fator R.
            </Text>
          )}
          {produto && (
            <Flex gap={5} mt={3} wrap="wrap">
              <Checkbox isChecked={f.icms_st} onChange={e => set({ icms_st: e.target.checked })}>
                ICMS por substituição tributária
              </Checkbox>
              <Checkbox
                isChecked={f.monofasico}
                onChange={e => set({ monofasico: e.target.checked })}
              >
                PIS/COFINS monofásico
              </Checkbox>
            </Flex>
          )}
          <Checkbox
            mt={3}
            isChecked={f.exterior}
            onChange={e => set({ exterior: e.target.checked })}
          >
            Exportação / tomador no exterior
          </Checkbox>
          <FormControl mt={3}>
            <FormLabel fontSize="12px" mb={1}>
              Observação
            </FormLabel>
            <Textarea
              size="sm"
              value={f.observacao ?? ''}
              onChange={e => set({ observacao: e.target.value || null })}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={!valido}
            isLoading={salvar.isPending}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function FaturasSimplesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const podeLancar = hasPermission(TELA_FATURAS_PATH, 'lancar');
  const tour = useTour('supervisor-simples-faturas');
  const { currentDate, handlePrevMonth, handleNextMonth, handleSelectMonth } = useMonthFilter();
  const competencia = currentDate.clone().startOf('month').format('YYYY-MM-DD');
  const [texto, setTexto] = useState('');
  const [busca] = useDebounceValue(texto, 400);
  const [editando, setEditando] = useState<IFatura | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sn-faturas', competencia, busca],
    queryFn: () => snApi.faturas({ competencia, search: busca || undefined }),
  });
  const excluir = useMutation({
    mutationFn: (id: number) => snApi.excluirFatura(id),
    onSuccess: () => {
      toast.success('Fatura excluída e apuração recalculada.');
      qc.invalidateQueries({ queryKey: ['sn-faturas'] });
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const porEmpresa = useMemo(
    () => new Set((data?.items ?? []).map(f => f.empresa_id)).size,
    [data],
  );

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/supervisor/simples" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
          >
            <FileText size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Supervisor › Simples Nacional
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" color="#0F172A">
              Faturas (receita sem nota)
            </Text>
          </Box>
          <Box flex={1} />
          {isFetching && <Spinner size="sm" color="#1B88F5" />}
          <Button size="sm" variant="ghost" leftIcon={<Question size={16} />} onClick={tour.abrir}>
            Como funciona
          </Button>
          <Box data-tour="mes">
            <MonthNavigator
              currentDate={currentDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </Box>
        </Flex>

        <GlassCard intensity="strong" p="12px 14px" overflow="visible">
          <Flex gap={3} wrap="wrap" align="center">
            <InputGroup size="sm" w={{ base: 'full', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={15} color="#94A3B8" />
              </InputLeftElement>
              <Input
                borderRadius="10px"
                placeholder="Empresa, descrição, tomador, nº"
                value={texto}
                onChange={e => setTexto(e.target.value)}
              />
            </InputGroup>
            <Text fontSize="12px" color="#64748B" fontWeight={600}>
              {data?.total ?? 0} fatura(s) · {porEmpresa} empresa(s) · {brl(data?.valor_total ?? 0)}
            </Text>
            <Box flex={1} />
            {podeLancar && (
              <Button
                size="sm"
                colorScheme="blue"
                borderRadius="10px"
                leftIcon={<Plus size={15} />}
                onClick={() => setEditando(VAZIA(0, competencia))}
                data-tour="nova"
              >
                Nova fatura
              </Button>
            )}
          </Flex>
        </GlassCard>

        <GlassCard intensity="strong" p={0} data-tour="lista">
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="#1B88F5" />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Empresa</Th>
                    <Th>Data</Th>
                    <Th data-tour="tipo">Tipo</Th>
                    <Th>Descrição</Th>
                    <Th>Tomador</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Lançado por</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {(data?.items ?? []).length === 0 && (
                    <Tr>
                      <Td colSpan={8} textAlign="center" color="#64748B" py={8}>
                        Nenhuma fatura neste mês.
                      </Td>
                    </Tr>
                  )}
                  {(data?.items ?? []).map(f => (
                    <Tr key={f.id}>
                      <Td maxW="220px">
                        <Text noOfLines={1} fontSize="13px">
                          {f.empresa?.nome}
                        </Text>
                        <Text fontSize="11px" color="#64748B">
                          ID {f.empresa?.external_id}
                        </Text>
                      </Td>
                      <Td>{dataBr(f.data)}</Td>
                      <Td>
                        <Badge variant="subtle" colorScheme={COR_TIPO[f.tipo]}>
                          {TIPOS[f.tipo]}
                        </Badge>
                        {f.item_lc116 && (
                          <Text fontSize="11px" color="#64748B">
                            LC 116 {f.item_lc116}
                            {f.iss_retido ? ' · ISS retido' : ''}
                          </Text>
                        )}
                        {(f.icms_st || f.monofasico || f.exterior) && (
                          <Text fontSize="11px" color="#64748B">
                            {[
                              f.icms_st && 'ST',
                              f.monofasico && 'monofásico',
                              f.exterior && 'exterior',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Text>
                        )}
                      </Td>
                      <Td maxW="240px">
                        <Text noOfLines={2} fontSize="13px">
                          {f.descricao}
                        </Text>
                        {f.numero_documento && (
                          <Text fontSize="11px" color="#64748B">
                            Nº {f.numero_documento}
                          </Text>
                        )}
                      </Td>
                      <Td maxW="180px">
                        <Text noOfLines={1} fontSize="13px">
                          {f.tomador_nome ?? '—'}
                        </Text>
                      </Td>
                      <Td isNumeric fontWeight={600}>
                        {brl(f.valor)}
                      </Td>
                      <Td fontSize="12px" color="#64748B">
                        {f.usuario_nome}
                      </Td>
                      <Td>
                        {podeLancar && (
                          <Flex gap={1}>
                            <Tooltip label="Editar" fontSize="11px">
                              <IconButton
                                aria-label="Editar"
                                size="xs"
                                variant="ghost"
                                icon={<PencilSimple size={14} />}
                                onClick={() => setEditando(f)}
                              />
                            </Tooltip>
                            <Tooltip label="Excluir" fontSize="11px">
                              <IconButton
                                aria-label="Excluir"
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                icon={<Trash size={14} />}
                                isLoading={excluir.isPending && excluir.variables === f.id}
                                onClick={() => {
                                  const msg = 'Excluir esta fatura? O mês será recalculado.';
                                  // eslint-disable-next-line no-alert
                                  if (window.confirm(msg)) {
                                    excluir.mutate(f.id as number);
                                  }
                                }}
                              />
                            </Tooltip>
                          </Flex>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </GlassCard>
      </Flex>
      {editando && (
        <FaturaModal
          inicial={editando}
          empresaInicial={editando.empresa ?? null}
          competencia={competencia}
          onFechar={() => setEditando(null)}
        />
      )}
      <Tour passos={PASSOS} aberto={tour.aberto} onFechar={tour.fechar} />
    </GlassPageBg>
  );
}
